/**
 * Space Switcher
 *
 * Dropdown in the sidebar that lets users switch between their personal space,
 * team spaces, and the org-wide space. Shows the current space name and type.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Users,
  Building2,
  ChevronDown,
  Check,
  Plus,
  Layers,
  Settings,
  AlertTriangle,
} from 'lucide-react';
import { useSpaceStore } from '../../stores/prioritization/spaceStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import type { MySpace, SpaceType } from '../../types/prioritization/collaborationSpaces';
import { SPACE_TYPE_LABELS } from '../../types/prioritization/collaborationSpaces';
import { useToast } from '../ui/Toast';
import { createSpace } from '../../lib/spaceService';

// ── Icons per space type ───────────────────────────────────────────────────────

function SpaceIcon({ type, className }: { type: SpaceType; className?: string }) {
  const cls = className ?? 'h-4 w-4';
  if (type === 'personal') return <User className={cls} />;
  if (type === 'team') return <Users className={cls} />;
  return <Building2 className={cls} />;
}

// ── Main component ─────────────────────────────────────────────────────────────

export function SpaceSwitcher({
  iconOnly = false,
}: {
  /** Icon-rail sidebar: space-type icon + tooltip, dropdown opens to the right. */
  iconOnly?: boolean;
} = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const {
    currentSpaceId,
    mySpaces,
    isLoading,
    fetchSpaces,
    bootstrapSpaces,
    setCurrentSpace,
    getSpacesByType,
  } = useSpaceStore();
  const bootstrapStatus = useSpaceStore((s) => s.bootstrapStatus);
  const retryBootstrap = useSpaceStore((s) => s.retryBootstrap);

  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspaceResolutionComplete = useTenantStore((s) => s.workspaceResolutionComplete);
  const permissionsLoaded = useUserStore((s) => s.permissionsLoaded);
  const userId = useUserStore((s) => s.userId);
  const { showToast } = useToast();

  const currentSpace = mySpaces.find(s => s.id === currentSpaceId);

  // Bootstrap spaces once auth + workspace context are ready (retry when permissions load).
  useEffect(() => {
    if (!workspaceResolutionComplete || !permissionsLoaded) return;
    if (!currentWorkspaceId || !userId || userId === 'unknown') return;
    void bootstrapSpaces(currentWorkspaceId);
  }, [currentWorkspaceId, userId, permissionsLoaded, workspaceResolutionComplete, bootstrapSpaces]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(spaceId: string) {
    if (spaceId === currentSpaceId) {
      setIsOpen(false);
      return;
    }
    setCurrentSpace(spaceId);
    setIsOpen(false);
    const selected = mySpaces.find(s => s.id === spaceId);
    if (selected) {
      showToast('success', `Switched to "${selected.name}"`, `Now working in your ${SPACE_TYPE_LABELS[selected.spaceType].toLowerCase()} space.`, 3000);
    }
  }

  const personalSpaces = getSpacesByType('personal');
  const teamSpaces = getSpacesByType('team');
  const orgSpaces = getSpacesByType('org');

  // "Select a space…" is a prompt to act, so it must only appear once we know
  // there is nothing selected. Until the bootstrap resolves we have no answer
  // yet — showing the prompt during that window (and forever after a failed
  // bootstrap) made a transient RPC error look like a lost workspace.
  const isResolving =
    mySpaces.length === 0 && (isLoading || bootstrapStatus === 'idle' || bootstrapStatus === 'running');

  if (isResolving) {
    return (
      <div
        className={`flex items-center text-sm text-[var(--text-ter)] ${
          iconOnly ? 'justify-center h-10 w-10' : 'gap-2 px-3 py-2'
        }`}
        title={iconOnly ? 'Loading spaces…' : undefined}
      >
        <Layers className="h-4 w-4 animate-pulse" />
        {!iconOnly && <span>Loading spaces…</span>}
      </div>
    );
  }

  if (bootstrapStatus === 'failed' && mySpaces.length === 0) {
    return (
      <button
        type="button"
        onClick={() => currentWorkspaceId && void retryBootstrap(currentWorkspaceId)}
        title="Couldn't load your spaces. Click to retry."
        aria-label="Couldn't load your spaces. Click to retry."
        className={
          iconOnly
            ? 'flex h-10 w-10 items-center justify-center  border border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors dark:border-[var(--mustard)]/40 dark:bg-[var(--mustard)]/10 dark:text-[var(--mustard)] dark:hover:bg-[var(--mustard)]/20'
            : 'w-full flex items-center gap-2 px-3 py-2 text-sm bg-[var(--mustard-dim)] border border-[var(--mustard)] text-[var(--mustard)]  hover:bg-[var(--mustard-dim)] transition-colors dark:border-[var(--mustard)]/40 dark:bg-[var(--mustard)]/10 dark:text-[var(--mustard)] dark:hover:bg-[var(--mustard)]/20'
        }
      >
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        {!iconOnly && (
          <div className="flex-1 text-left min-w-0">
            <div className="font-medium truncate">Spaces unavailable</div>
            <div className="text-xs">Tap to retry</div>
          </div>
        )}
      </button>
    );
  }

  const spaceLabel = currentSpace
    ? `${currentSpace.name} (${SPACE_TYPE_LABELS[currentSpace.spaceType]} Space)`
    : 'Select a space…';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        title={iconOnly ? spaceLabel : undefined}
        aria-label={spaceLabel}
        className={
          iconOnly
            ? 'flex h-10 w-10 items-center justify-center  border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
            : 'w-full flex items-center gap-2 px-3 py-2 text-sm bg-[var(--bg-raised)] border border-[var(--rule)]  hover:bg-[var(--surface)] transition-colors'
        }
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {currentSpace ? (
          <>
            <SpaceIcon
              type={currentSpace.spaceType}
              className="h-4 w-4 text-primary-600 flex-shrink-0"
            />
            {!iconOnly && (
              <div className="flex-1 text-left min-w-0">
                <div className="font-medium text-[var(--text-pri)] truncate">{currentSpace.name}</div>
                <div className="text-xs text-[var(--text-ter)]">
                  {SPACE_TYPE_LABELS[currentSpace.spaceType]} Space
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <Layers className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
            {!iconOnly && (
              <div className="flex-1 text-left text-[var(--text-ter)]">Select a space…</div>
            )}
          </>
        )}
        {!iconOnly && (
          <ChevronDown
            className={`h-4 w-4 text-[var(--text-ter)] flex-shrink-0 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>

      {isOpen && (
        <div
          className={`absolute bg-[var(--bg-raised)] border border-[var(--rule)]  shadow-lg z-50 overflow-hidden dark:bg-[var(--bg-raised)] dark:border-[var(--rule)] ${
            iconOnly
              ? 'left-full top-0 ml-2 w-64'
              : 'bottom-full left-0 right-0 mb-1'
          }`}
        >
          <div className="p-1 max-h-72 overflow-y-auto">

            {/* Personal spaces */}
            {personalSpaces.length > 0 && (
              <SpaceGroup
                label="Personal"
                spaces={personalSpaces}
                currentSpaceId={currentSpaceId}
                onSelect={handleSelect}
              />
            )}

            {/* Team spaces */}
            {teamSpaces.length > 0 && (
              <SpaceGroup
                label="Teams"
                spaces={teamSpaces}
                currentSpaceId={currentSpaceId}
                onSelect={handleSelect}
              />
            )}

            {/* Org spaces */}
            {orgSpaces.length > 0 && (
              <SpaceGroup
                label="Organization"
                spaces={orgSpaces}
                currentSpaceId={currentSpaceId}
                onSelect={handleSelect}
              />
            )}

            {mySpaces.length === 0 && (
              <div className="px-3 py-4 text-sm text-[var(--text-ter)] text-center">
                No spaces yet
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="border-t border-[var(--rule)] p-1">
            {showQuickCreate ? (
              <QuickCreateSpace
                workspaceId={currentWorkspaceId ?? ''}
                userId={userId}
                onCreated={(spaceId) => {
                  setShowQuickCreate(false);
                  setIsOpen(false);
                  if (currentWorkspaceId) fetchSpaces(currentWorkspaceId);
                  setCurrentSpace(spaceId);
                  showToast('success', 'Space created', 'You\'re now working in your new space.', 3000);
                }}
                onCancel={() => setShowQuickCreate(false)}
              />
            ) : (
              <div className="flex gap-1">
                <button
                  onClick={() => setShowQuickCreate(true)}
                  className="flex-1 flex items-center gap-1.5 px-2 py-1.5 text-xs text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  New Space
                </button>
                <button
                  onClick={() => { setIsOpen(false); navigate('/prioritization/settings/spaces'); }}
                  className="flex-1 flex items-center gap-1.5 px-2 py-1.5 text-xs text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
                >
                  <Settings className="h-3.5 w-3.5" />
                  Manage
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Quick-create mini form ─────────────────────────────────────────────────────

function QuickCreateSpace({
  workspaceId,
  userId,
  onCreated,
  onCancel,
}: {
  workspaceId: string;
  userId: string;
  onCreated: (spaceId: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [spaceType, setSpaceType] = useState<'personal' | 'team'>('personal');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !workspaceId) return;
    setSubmitting(true);
    setError(null);
    const space = await createSpace({
      workspaceId,
      name: name.trim(),
      spaceType,
      ownerId: spaceType === 'personal' ? userId : undefined,
      allowGuests: true,
    });
    setSubmitting(false);
    if (space) {
      onCreated(space.id);
    } else {
      setError('Could not create space.');
    }
  }

  return (
    <form onSubmit={handleCreate} className="px-2 py-2 space-y-2">
      {/* Type toggle */}
      <div className="flex gap-1">
        {(['personal', 'team'] as const).map(t => (
          <button
            key={t}
            type="button"
            onClick={() => setSpaceType(t)}
            className={`flex-1 flex items-center justify-center gap-1 py-1 text-xs rounded border transition-colors ${
              spaceType === t
                ? 'border-primary-300 bg-primary-50 text-primary-700 font-medium'
                : 'border-[var(--rule)] text-[var(--text-ter)] hover:bg-[var(--surface)]'
            }`}
          >
            {t === 'personal' ? <User className="h-3 w-3" /> : <Users className="h-3 w-3" />}
            {t === 'personal' ? 'Individual' : 'Team'}
          </button>
        ))}
      </div>

      {/* Name input */}
      <input
        autoFocus
        type="text"
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder={spaceType === 'personal' ? 'My Space' : 'Team name…'}
        className="w-full text-xs border border-[var(--rule)] rounded px-2 py-1.5 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
      />

      {error && <p className="text-xs text-[var(--coral)]">{error}</p>}

      <div className="flex gap-1 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-[var(--text-ter)] px-2 py-1 rounded hover:bg-[var(--hover-tint)]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || !name.trim()}
          className="text-xs bg-primary-600 hover:bg-primary-700 text-white px-3 py-1 rounded font-medium disabled:opacity-50"
        >
          {submitting ? 'Creating…' : 'Create'}
        </button>
      </div>
    </form>
  );
}

// ── Space group sub-component ─────────────────────────────────────────────────

function SpaceGroup({
  label,
  spaces,
  currentSpaceId,
  onSelect,
}: {
  label: string;
  spaces: MySpace[];
  currentSpaceId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="mb-1">
      <div className="px-2 py-1.5 text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
        {label}
      </div>
      {spaces.map(space => (
        <button
          key={space.id}
          onClick={() => onSelect(space.id)}
          className={`w-full flex items-center gap-2 px-2 py-2  text-left transition-colors ${
            space.id === currentSpaceId
              ? 'bg-primary-50 text-primary-700'
              : 'text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
          }`}
          role="option"
          aria-selected={space.id === currentSpaceId}
        >
          <SpaceIcon
            type={space.spaceType}
            className={`h-4 w-4 flex-shrink-0 ${
              space.id === currentSpaceId ? 'text-primary-600' : 'text-[var(--text-ter)]'
            }`}
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{space.name}</div>
            <div className="text-xs text-[var(--text-ter)] capitalize">{space.memberRole}</div>
          </div>
          {space.id === currentSpaceId && (
            <Check className="h-4 w-4 text-primary-600 flex-shrink-0" />
          )}
        </button>
      ))}
    </div>
  );
}
